import {
  front_base_url,
  configurationModuleType,
  mc80_range_id,
  tm172sig_type,
  m172_ranges,
  m172_battery_backup,
  m171_need_amb,
  pacdrive_ranges,
  tm171_communication_module_type,
  m171_ranges,
  clusterTerminationData,
} from "../../constants";
import Configuration from "../../models/Configuration";
import Device from "../../models/Device";
import I10n from "../i10n";

const canvasHeight = 800;
const canVerticalOffset = 100;
const rs485VerticalOffset = 200;
const widthWiringLine = 16;
const gapBetweenModules = 100;
const rs485Color = "#43CE5D";
const canColor = "#42B4E6";

const getModuleAtSlot = (configuration, slot, moduleType) => {
  let mod;
  switch (moduleType) {
    case configurationModuleType.head_module:
      mod = configuration.headModule;
      break;
    case configurationModuleType.com_module:
      mod = configuration.comModules.find((m) => m.slot === slot);
      break;
    case configurationModuleType.io_module:
      mod = configuration.ioModules.find((m) => m.slot === slot);
      break;
    default:
      break;
  }

  return new Device(mod);
};

const canShowPacdriveWiring = (configuration) => {
  return (
    pacdrive_ranges.includes(configuration.headModule.rangeOfProduct) &&
    configuration.ioModules?.length > 0
  );
};

const canShowM172Wiring = (configuration) => {
  return m172_ranges.includes(configuration.headModule.rangeOfProduct) || m171_ranges.includes(configuration.headModule.rangeOfProduct);
};

const addImageProcess = (src) => {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.onload = () => resolve(img);
    img.setAttribute("crossorigin", "anonymous");
    img.onerror = () => {
      console.warn(`Failed to load image from ${src}`);
      reject();
    };
    img.src = `${src}`;
  });
};

const scaleImage = (img, mod, canvasHeight, maxModuleHeight) => {
  const heightRatio = Math.ceil((100 * mod.height) / maxModuleHeight) / 100;
  img.height = canvasHeight * (heightRatio ? heightRatio : 1);
  const aspectRatio = mod.width / mod.height;
  img.width = img.height * aspectRatio.toFixed(2);
  return img;
};

const widthEmptySlot = (
  slotWidth,
  slotHeight,
  canvasHeight,
  maxModuleHeight
) => {
  const heightRatio = Math.ceil((100 * slotHeight) / maxModuleHeight) / 100;
  const newHeight = canvasHeight * heightRatio;
  const aspectRatio = slotWidth / slotHeight;

  return newHeight * aspectRatio.toFixed(2);
};

const drawSIGWiring = (
  ctx,
  island,
  mod,
  img,
  offsetX,
  lastM172SIGModuleSlot
) => {
  if (mod.moduleType === configurationModuleType.head_module) {
    ctx.beginPath();
    ctx.strokeStyle = rs485Color;
    ctx.lineWidth = widthWiringLine;
    ctx.moveTo(offsetX + img.width / 2.0, img.height);
    ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + rs485VerticalOffset);
    ctx.lineTo(offsetX, canvasHeight + rs485VerticalOffset);
    ctx.stroke();
    ctx.closePath();
  }

  if (mod.moduleType === configurationModuleType.com_module) {
     if (mod.slot < lastM172SIGModuleSlot) {
      // Draw horizontal line
      ctx.beginPath();
      ctx.strokeStyle = rs485Color;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX, canvasHeight + rs485VerticalOffset);
      ctx.lineTo(
        offsetX + img.width + gapBetweenModules,
        canvasHeight + rs485VerticalOffset
      );
      ctx.stroke();
      ctx.closePath();

      if (tm171_communication_module_type === mod.productType ) {
        // Draw vertical
        ctx.beginPath();
        ctx.strokeStyle =  rs485Color;
        ctx.lineWidth = widthWiringLine;
        ctx.moveTo(offsetX + img.width / 2.0, img.height);
        ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + rs485VerticalOffset);
        ctx.stroke();
        ctx.closePath();
      }
    } else if (
      tm171_communication_module_type !== mod.productType &&
      mod.slot === lastM172SIGModuleSlot
    ) {
      ctx.beginPath();
      ctx.strokeStyle = rs485Color;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX + img.width / 2.0, img.height);
      ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + rs485VerticalOffset);
      ctx.lineTo(
        offsetX + img.width + gapBetweenModules,
        canvasHeight + rs485VerticalOffset
      );
      ctx.stroke();
      ctx.closePath();
      ctx.font = "48px sans-serif";
      ctx.fillStyle = rs485Color;
      ctx.fillText(
        I10n.get("screen.wiring.rs485"),
        offsetX + img.width / 2.0 + widthWiringLine,
        canvasHeight + rs485VerticalOffset - widthWiringLine
      );
    }
  }
};

const drawPacdriveWiring = (ctx, mod, img, offsetX, lastModuleSlot) => {
  if (mod.moduleType === configurationModuleType.head_module) {
    ctx.beginPath();
    ctx.strokeStyle = canColor;
    ctx.lineWidth = widthWiringLine;
    ctx.moveTo(offsetX + img.width / 2.0, img.height);
    ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + canVerticalOffset);
    ctx.lineTo(
      offsetX + img.width + gapBetweenModules,
      canvasHeight + canVerticalOffset
    );
    ctx.stroke();
    ctx.closePath();

    ctx.font = "48px sans-serif";
    ctx.fillStyle = canColor;
    ctx.fillText(
      I10n.get("screen.wiring.pacnet"),
      offsetX + img.width / 2.0 + widthWiringLine,
      canvasHeight + canVerticalOffset - widthWiringLine
    );
  } else if (mod.moduleType === configurationModuleType.io_module) {
    if (mod.slot < lastModuleSlot) {
      // Draw horizontal
      ctx.beginPath();
      ctx.strokeStyle = canColor;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX, canvasHeight + canVerticalOffset);
      ctx.lineTo(
        offsetX + img.width + gapBetweenModules,
        canvasHeight + canVerticalOffset
      );
      ctx.stroke();
      ctx.closePath();
      // Draw vertical
      ctx.beginPath();
      ctx.strokeStyle = canColor;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX + img.width / 2.0, img.height);
      ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + canVerticalOffset);
      ctx.stroke();
      ctx.closePath();
    } else if (mod.slot === lastModuleSlot) {
      ctx.beginPath();
      ctx.strokeStyle = canColor;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX + img.width / 2.0, img.height);
      ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + canVerticalOffset);
      ctx.lineTo(offsetX, canvasHeight + canVerticalOffset);
      ctx.stroke();
      ctx.closePath();
    }
  }
};

const drawM172Wiring = (
  ctx,
  mod,
  img,
  offsetX,
  lastM172RS485ModuleSlot,
  lastNonM172RS485ModuleSlot,
  lastM172SIGModuleSlot
) => {
  if (mod.moduleType === configurationModuleType.head_module) {
    if (lastM172RS485ModuleSlot > -1) {
      ctx.beginPath();
      ctx.strokeStyle = rs485Color;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX + img.width / 2.0, img.height);
      ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + rs485VerticalOffset);
      ctx.lineTo(
        offsetX + img.width + gapBetweenModules,
        canvasHeight + rs485VerticalOffset
      );
      ctx.stroke();
      ctx.closePath();

      if (lastM172SIGModuleSlot === -1 || m171_need_amb === mod.productType) {
        ctx.font = "48px sans-serif";
        ctx.fillStyle = rs485Color;
        ctx.fillText(
          I10n.get("screen.wiring.rs485"),
          offsetX + img.width / 2.0 + widthWiringLine,
          canvasHeight + rs485VerticalOffset - widthWiringLine
        );
      }
    }

    if (lastNonM172RS485ModuleSlot > -1) {
      ctx.beginPath();
      ctx.strokeStyle = canColor;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX + img.width / 2.0, img.height);
      ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + canVerticalOffset);
      ctx.lineTo(
        offsetX + img.width + gapBetweenModules,
        canvasHeight + canVerticalOffset
      );
      ctx.stroke();
      ctx.closePath();

      ctx.font = "48px sans-serif";
      ctx.fillStyle = canColor;

      ctx.fillText(
        m171_ranges.includes(mod.rangeOfProduct) ? I10n.get("screen.wiring.lan") :  I10n.get("screen.wiring.canbus"),
        offsetX + img.width / 2.0 + widthWiringLine,
        canvasHeight + canVerticalOffset - widthWiringLine
      );
    }
  } else if (mod.moduleType === configurationModuleType.io_module) {
    // Draw RS455
    if (mod.slot < lastM172RS485ModuleSlot) {
      // Draw horizontal
      ctx.beginPath();
      ctx.strokeStyle = rs485Color;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX, canvasHeight + rs485VerticalOffset);
      ctx.lineTo(
        offsetX + img.width + gapBetweenModules,
        canvasHeight + rs485VerticalOffset
      );
      ctx.stroke();
      ctx.closePath();
      // Draw vertical
      if (mod.productType !== m172_battery_backup && mod.isM172RS485Module()) {
        ctx.beginPath();
        ctx.strokeStyle = rs485Color;
        ctx.lineWidth = widthWiringLine;
        ctx.moveTo(offsetX + img.width / 2.0, img.height);
        ctx.lineTo(
          offsetX + img.width / 2.0,
          canvasHeight + rs485VerticalOffset
        );
        ctx.stroke();
        ctx.closePath();
      }
    } else if (mod.slot === lastM172RS485ModuleSlot) {
      ctx.beginPath();
      ctx.strokeStyle = rs485Color;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX + img.width / 2.0, img.height);
      ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + rs485VerticalOffset);
      ctx.lineTo(offsetX, canvasHeight + rs485VerticalOffset);
      ctx.stroke();
      ctx.closePath();
    }

    // Draw Can
    if (mod.slot < lastNonM172RS485ModuleSlot) {
      // Draw horizontal
      ctx.beginPath();
      ctx.strokeStyle = canColor;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX, canvasHeight + canVerticalOffset);
      ctx.lineTo(
        offsetX + img.width + gapBetweenModules,
        canvasHeight + canVerticalOffset
      );
      ctx.stroke();
      ctx.closePath();
      // Draw vertical
      if (mod.productType !== m172_battery_backup && !mod.isM172RS485Module()) {
        ctx.beginPath();
        ctx.strokeStyle = canColor;
        ctx.lineWidth = widthWiringLine;
        ctx.moveTo(offsetX + img.width / 2.0, img.height);
        ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + canVerticalOffset);
        ctx.stroke();
        ctx.closePath();
      }
    } else if (mod.slot === lastNonM172RS485ModuleSlot) {
      ctx.beginPath();
      ctx.strokeStyle = canColor;
      ctx.lineWidth = widthWiringLine;
      ctx.moveTo(offsetX + img.width / 2.0, img.height);
      ctx.lineTo(offsetX + img.width / 2.0, canvasHeight + canVerticalOffset);
      ctx.lineTo(offsetX, canvasHeight + canVerticalOffset);
      ctx.stroke();
      ctx.closePath();
    }
  }
};

export const generateIslandImage = async (island) => {
  const configuration = new Configuration(island);
  let canvasWidth = 0;

  const maxModuleHeight = configuration.maxModuleHeight();
  const blocks = configuration.blocks();
  const moduleImages = {};
  const blockOffset = 50;

  // Preload image for blocks
  for (const block of blocks) {
    for (const mod of block.modules) {
      try {
        let img;
        if (!moduleImages[mod.id]) {
          let tmpImg;
          try {
            tmpImg = await addImageProcess(
              `${front_base_url}/images/devices-images/${mod.id}.png`
            );
          } catch (err) {
            tmpImg = await addImageProcess(mod.sidePicture);
          }
          moduleImages[mod.id] = scaleImage(
            tmpImg,
            mod,
            canvasHeight,
            maxModuleHeight
          );
        }

        img = moduleImages[mod.id];
        // Some M172 modules are placed appart
        if (
          canShowPacdriveWiring(configuration) ||
          (canShowM172Wiring(configuration) &&
            mod.moduleType === configurationModuleType.io_module &&
            !(mod.productType === m172_battery_backup))
        ) {
          canvasWidth += gapBetweenModules;
        }

        // Load cartridges images, same dimensions as head module.
        if (mod.moduleType === configurationModuleType.head_module) {
          for (const cartridge of configuration.cartridges) {
            try {
              if (!moduleImages[cartridge.id]) {
                const cartridgeImage = await addImageProcess(
                  `${front_base_url}${mod.baseCartridgeURL}_${cartridge.id}_Slot${cartridge.slot}.png`
                );
                if (cartridgeImage) {
                  cartridgeImage.height = img.height;
                  cartridgeImage.width = img.width;
                  moduleImages[cartridge.id] = cartridgeImage;
                }
              }
            } catch (e) {
              console.log(e);
            }
          }
        } else {
          // Load terminal block images, only if not head module.
          for (const imgObj of mod.getSlottedDevicesFrontImageLinks()) {
            try {
              if (!moduleImages[imgObj.src]) {
                const slottedDeviceImage = await addImageProcess(
                  `${front_base_url}/images/devices-images/${imgObj.src}.png`
                );
                if (slottedDeviceImage) {
                  slottedDeviceImage.height = img.height;
                  slottedDeviceImage.width = img.width;
                  moduleImages[imgObj.src] = slottedDeviceImage;
                }
              }
            } catch (e) {
              console.log(e);
            }
          }
        }
        if (tm172sig_type === mod.productType) {
          canvasWidth += gapBetweenModules;
        }
        canvasWidth += img.width;
      } catch (e) {
        console.log(e);
      }
    }
  }

  const lastModule = configuration.modules().slice(-1)[0];
  const lastM172RS485ModuleSlot = configuration.findLastM172RS485ModuleSlot();
  const lastNonM172RS485ModuleSlot =
    configuration.findLastNonM172RS485ModuleSlot();
  const lastM172SIGModuleSlot = configuration.findLastM172SIGModuleSlot();

  let fullHeigth = canvasHeight;
  if (
    canShowM172Wiring(configuration) &&
    (lastM172SIGModuleSlot !== -1 || lastM172RS485ModuleSlot !== -1)
  ) {
    fullHeigth += rs485VerticalOffset + widthWiringLine / 2.0;
  } else if (
    (canShowM172Wiring(configuration) && lastNonM172RS485ModuleSlot !== -1) ||
    canShowPacdriveWiring(configuration)
  ) {
    fullHeigth += canVerticalOffset + widthWiringLine / 2.0;
  }

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth + (blocks.length - 1) * blockOffset;
  canvas.height = fullHeigth;
  const ctx = canvas.getContext("2d");
  let offsetX = 0;
  let previousHeight = 0;

  for (const [bidx, block] of blocks.entries()) {
    if (bidx > 0) {
      ctx.beginPath();
      ctx.strokeStyle = "#42B4E6";
      ctx.lineWidth = 50;
      ctx.moveTo(offsetX, previousHeight / 2);
      ctx.lineTo(offsetX + blockOffset, previousHeight / 2);
      ctx.stroke();
      ctx.closePath();
      offsetX += blockOffset;
    }
    for (const mod of block.modules) {
      const img = moduleImages[mod.id];
      if (img) {
        // Some M172 modules are placed appart
        if (
          canShowPacdriveWiring(configuration) ||
          (canShowM172Wiring(configuration) &&
            mod.moduleType === configurationModuleType.io_module &&
            !(mod.productType === m172_battery_backup))
        ) {
          offsetX += gapBetweenModules;
        }

        previousHeight = img.height;
        ctx.drawImage(img, offsetX, 0, img.width, img.height);

        if (configurationModuleType.head_module === mod.moduleType) {
          // Draw cartridges.
          for (const cartridge of configuration.cartridges) {
            const cartridgeImage = moduleImages[cartridge.id];
            if (cartridgeImage) {
              ctx.drawImage(
                cartridgeImage,
                offsetX,
                0,
                cartridgeImage.width,
                cartridgeImage.height
              );
            }
          }
        } else {
          for (const imgObj of mod.getSlottedDevicesFrontImageLinks()) {
            const slottedDeviceImage = moduleImages[imgObj.src];
            if (slottedDeviceImage) {
              ctx.drawImage(
                slottedDeviceImage,
                offsetX,
                0,
                slottedDeviceImage.width,
                slottedDeviceImage.height
              );
            }
          }
        }

        if (canShowPacdriveWiring(configuration)) {
          drawPacdriveWiring(ctx, mod, img, offsetX, lastModule?.slot);
        }

        if (lastM172SIGModuleSlot !== -1) {
          drawSIGWiring(
            ctx,
            configuration,
            mod,
            img,
            offsetX,
            lastM172SIGModuleSlot
          );
        }

        if (
          canShowM172Wiring(configuration) &&
          configuration.ioModules.length > 0
        ) {
          drawM172Wiring(
            ctx,
            mod,
            img,
            offsetX,
            lastM172RS485ModuleSlot,
            lastNonM172RS485ModuleSlot,
            lastM172SIGModuleSlot
          );
        }

        if (tm172sig_type === mod.productType) {
          offsetX += gapBetweenModules;
        }

        offsetX += img.width;
      }
    }
  }

  // return base 64 string of image.
  return canvas.toDataURL().split(",")[1];
};

export const generatePacIslandImage = async (island, isIsland = false, useLocalRackDisplay = false) => {
  const configuration = new Configuration(island);
  let canvasWidth = 0;
  const maxModuleHeight = configuration.maxModuleHeight();
  const moduleImages = {};
  let rackImg;

  const blockOffset = 50;

  // Preload images of Rack & RedBox
  try {
    const tmpImg = await addImageProcess(
      `${front_base_url}/images/devices-images/${configuration.rack.id}.png`
    );
    rackImg = scaleImage(
      tmpImg,
      configuration.rack,
      canvasHeight,
      maxModuleHeight
    );
    canvasWidth += rackImg.width;
  } catch (e) {
    console.log(e);
  }

  for (const redbox of configuration.redboxes) {
    try {
      if (!moduleImages[redbox.id]) {
        const tmpImg = await addImageProcess(
          `${front_base_url}/images/small-devices-images/${redbox.id}_h320px.png`
        );
        moduleImages[redbox.id] = scaleImage(
          tmpImg,
          redbox,
          canvasHeight,
          maxModuleHeight
        );
      }
    } catch (e) {
      console.log(e);
    }
  }

  // Preload Modules
  let modules = [
    ...configuration.comModules,
    configuration.headModule,
    ...configuration.ioModules,
  ];

  for (const mod of modules) {
    try {
      if (!moduleImages[mod.id]) {
        let tmpImg;
        try {
          tmpImg = await addImageProcess(
            `${front_base_url}/images/devices-images/${mod.id}.png`
          );
        } catch (err) {
          tmpImg = await addImageProcess(mod.sidePicture);
        }
        moduleImages[mod.id] = scaleImage(
          tmpImg,
          mod,
          canvasHeight,
          maxModuleHeight
        );
      }
    } catch (e) {
      console.log(e);
    }
  }


  // Calculate total width including redboxes and empty space
  let totalRedboxWidth = 0;
  for (const redbox of configuration.redboxes) {
    if (moduleImages[redbox.id]) {
      totalRedboxWidth += moduleImages[redbox.id].width;
    }
  }
  canvasWidth += totalRedboxWidth + blockOffset;

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");
  
  let offsetX = 0;

  // Draw Rack + Modules
  if (configuration.rangeInfo?.rangeId !== mc80_range_id) {
    ctx.drawImage(rackImg, offsetX, 0, rackImg.width, rackImg.height);
    offsetX = rackImg.width;
    if (useLocalRackDisplay && isIsland) {
      const img = moduleImages[configuration.headModule.id];
      if (img) {
        offsetX -= img.width;
        ctx.drawImage(img, offsetX, 0, img.width, img.height);
      }

      for (let i = configuration.rack.maxIoModules; i > 0; i--) {
        const modAtSlot = getModuleAtSlot(
          configuration,
          i,
          configurationModuleType.io_module
        );
        if (modAtSlot.id.length > 0) {
          const img = moduleImages[modAtSlot.id];
          if (img) {
            offsetX -= img.width;
            ctx.drawImage(img, offsetX, 0, img.width, img.height);
          }
        } else {
          offsetX -= widthEmptySlot(
            32,
            configuration.rack.height,
            canvasHeight,
            maxModuleHeight
          );
        }
      }

      for (const mod of configuration.comModules) {
        const img = moduleImages[mod.id];
        if (img) {
          offsetX -= img.width;
          ctx.drawImage(img, offsetX, 0, img.width, img.height);
        }
      }
    } else {
      for (let i = configuration.rack.maxIoModules; i > 0; i--) {
        const modAtSlot = getModuleAtSlot(
          configuration,
          i,
          configurationModuleType.io_module
        );
        if (modAtSlot.id.length > 0) {
          const img = moduleImages[modAtSlot.id];
          if (img) {
            offsetX -= img.width;
            ctx.drawImage(img, offsetX, 0, img.width, img.height);
          }
        } else {
          offsetX -= widthEmptySlot(
            32,
            configuration.rack.height,
            canvasHeight,
            maxModuleHeight
          );
        }
      }

      const plcImg = moduleImages[configuration.headModule.id];
      if (plcImg) {
        offsetX -= plcImg.width;
        ctx.drawImage(plcImg, offsetX, 0, plcImg.width, plcImg.height);
      }

      for (const mod of configuration.comModules) {
        const img = moduleImages[mod.id];
        if (img) {
          offsetX -= img.width;
          ctx.drawImage(img, offsetX, 0, img.width, img.height);
        }
      }
    }
  } else {
    // Draw MC80
    const img = moduleImages[configuration.headModule.id];
    if (img) {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, offsetX, 0, img.width, img.height);
    }
  }

  // Add empty space for redboxes
  offsetX = rackImg.width + blockOffset;

  // Draw RedBox
  for (const redbox of configuration.redboxes) {
    const img = moduleImages[redbox.id];
    if (img) {
      ctx.drawImage(img, offsetX, 0, img.width, img.height);
      offsetX += img.width;
    }
  }

  // Return base64 string of image
  return canvas.toDataURL().split(",")[1];
};

export const generateSLCImage = async (slc) => {
  const tmpImg = await addImageProcess(
    `${front_base_url}/images/devices-images/${slc.id}.png`
  );
  const img = scaleImage(tmpImg, { height: slc.height, width: slc.width }, canvasHeight, slc.height);
  const canvas = document.createElement("canvas");
  canvas.width = img?.width || 0;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(img, 0, 0, img.width, img.height);

  return canvas.toDataURL().split(",")[1];
};

const renderEdgeWiring = (idxInBlock, nbBlocks, offsetX, img, isLastBlock, isFirstBlock, ctx, maxImageHeight) =>  {

  // Render wiring
  if (idxInBlock === 0 && nbBlocks > 1) {
    if (isLastBlock) {
      ctx.beginPath();
      ctx.strokeStyle = "#42B4E6";
      ctx.lineWidth = 30;
      ctx.moveTo(offsetX + (img.width / 2.0), img.height);
      ctx.lineTo(offsetX + (img.width / 2.0), img.height + (30 + (maxImageHeight - img.height)));
      ctx.lineTo(offsetX, img.height + (30 + (maxImageHeight - img.height)));
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.strokeStyle = "#42B4E6";
      ctx.lineWidth = 30;
      ctx.moveTo(offsetX + (img.width / 2.0), img.height);
      ctx.lineTo(offsetX + (img.width / 2.0), img.height + (30 + (maxImageHeight - img.height)));
      ctx.lineTo(offsetX + (img.width) + 50, img.height + (30 + (maxImageHeight - img.height)));
      ctx.stroke();
    }
  }

  if (!isLastBlock && (idxInBlock > 0 || !isFirstBlock)) {
    ctx.beginPath();
    ctx.strokeStyle = "#42B4E6";
    ctx.lineWidth = 30;
    ctx.moveTo(offsetX, img.height + (30 + (maxImageHeight - img.height)));
    ctx.lineTo(offsetX + (img.width) + 50, img.height + (30 + (maxImageHeight - img.height)));
    ctx.stroke();
  }

}

export const generateEdgeIslandImage = async (island) => {

  const configuration = new Configuration(island)
  let canvasWidth = 0;

  const maxModuleHeight = configuration.maxModuleHeight();
  const blocks = configuration.blocks() || [];
  const nbBlocks = blocks.length;
  const moduleImages = {};
  const blockOffset = 50;
  let maxImageHeight = 0;

  // Preload image of cluster termination
  try {
    let img;
    const mod = clusterTerminationData;
    const tmpImg = await addImageProcess(`${front_base_url}/images/devices-images/${mod.id}.png`);
    moduleImages[mod.id] = scaleImage(tmpImg, mod, canvasHeight, maxModuleHeight);
    img = moduleImages[mod.id];
    if (img.height > maxImageHeight) {
      maxImageHeight = img.height;
    }
  } catch(e) {}

  for (const block of blocks) {
    for (const mod of block.modules) {
      try {
        let img;
        if (!moduleImages[mod.id]) {
          const tmpImg = await addImageProcess(`${front_base_url}/images/devices-images/${mod.id}.png`);
          moduleImages[mod.id] = scaleImage(tmpImg, mod, canvasHeight, maxModuleHeight);
        }

        img = moduleImages[mod.id];

        // Load terminal block images, only if not head module.
        for (const imgObj of mod.getSlottedDevicesFrontImageLinks()) {
          try {
            if (!moduleImages[imgObj.src]) {
              const slottedDeviceImage = await addImageProcess(`${front_base_url}/images/devices-images/${imgObj.src}.png`);
              if (slottedDeviceImage) {
                slottedDeviceImage.height = img.height;
                slottedDeviceImage.width = img.width;
                moduleImages[imgObj.src] = slottedDeviceImage;
              }
            }
          } catch (e) {
            console.log(e);
          }
        }

        if (img.height > maxImageHeight) {
          maxImageHeight = img.height;
        }

        canvasWidth += img.width;
      } catch (e) {
        console.log(e);
      }
    }

    // Add cluster termination width to canvas
    const canvasTerminationImg = moduleImages[clusterTerminationData.id];
    if (canvasTerminationImg) {
      canvasWidth += canvasTerminationImg.width;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth + ((blocks.length - 1) * blockOffset);
  canvas.height = canvasHeight + 100;
  const ctx = canvas.getContext("2d");
  let offsetX = 0;

  for (const [bidx, block] of blocks.entries()) {
    const isLastBlock = ((bidx + 1) === nbBlocks);
    const isFirstBlock = bidx === 0;
    const modules = block.modules || [];
    if (bidx > 0) {
      offsetX += blockOffset;
    }
    for (let idxInBlock = 0; idxInBlock < modules.length; idxInBlock++) {
      const mod = block.modules[idxInBlock];
      const img = moduleImages[mod.id];

      if (img) {
        ctx.drawImage(img, offsetX, 0, img.width, img.height);
        for (const imgObj of mod.getSlottedDevicesFrontImageLinks()) {
          const slottedDeviceImage = moduleImages[imgObj.src];
          if (slottedDeviceImage) {
            ctx.drawImage(slottedDeviceImage, offsetX, 0, slottedDeviceImage.width, slottedDeviceImage.height);
          }
        }

        renderEdgeWiring(idxInBlock, nbBlocks, offsetX, img, isLastBlock, isFirstBlock, ctx, maxImageHeight);

        offsetX += img.width;
      }
    }

    // Draw cluster termination
    const img = moduleImages[clusterTerminationData.id];
    if (img) {
      ctx.drawImage(img, offsetX, 0, img.width, img.height);
      renderEdgeWiring(modules.length, nbBlocks, offsetX, img, isLastBlock, isFirstBlock, ctx, maxImageHeight);
      offsetX += img.width;
    }

  }

  // return base 64 string of image.
  return canvas.toDataURL().split(',')[1];

}
